import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'ebo-input',
  templateUrl: './ebo-input.component.html',
  styleUrls: ['./ebo-input.component.scss']
})
export class EboInputComponent implements OnInit {
  @Input() label: string;
  @Input() placeholder = '';
  @Input() type = 'text';
  @Input() required = false;
  @Input() hint = '';
  @Input() control: FormControl;

  // --- Chips mode ---
  /** When true the control holds removable chips (control value is a string[]) */
  @Input() hasChips = false;
  /** Keys (besides Enter) that commit the current text as a chip */
  @Input() separatorKeys: string[] = [',', 'Enter'];
  /** Reject duplicate chips (case-insensitive) */
  @Input() uniqueChips = true;

  isFocused = false;
  /** Pending text typed in chips mode (not yet committed to a chip) */
  inputValue = '';

  ngOnInit(): void {
    if (this.required && this.control) {
      this.control.addValidators(Validators.required);
      this.control.updateValueAndValidity();
    }
  }

  // =================================================================
  // PLAIN TEXT MODE
  // =================================================================
  onInputChange(event: any): void {
    if (this.control) {
      const raw = event.target.value;
      const value = this.type === 'number' ? (raw === '' ? null : parseFloat(raw)) : raw;
      this.control.setValue(value);
      this.control.markAsTouched();
    }
  }

  // =================================================================
  // CHIPS MODE
  // =================================================================
  get chips(): string[] {
    return Array.isArray(this.control?.value) ? this.control.value : [];
  }

  onChipInput(event: any): void {
    this.inputValue = event.target.value;
  }

  onChipKeyDown(event: KeyboardEvent): void {
    if (this.separatorKeys.includes(event.key)) {
      event.preventDefault();
      this.addChip(this.inputValue);
      return;
    }
    // Backspace on empty input removes the last chip
    if (event.key === 'Backspace' && !this.inputValue && this.chips.length) {
      this.removeChip(this.chips.length - 1);
    }
  }

  addChip(raw: string): void {
    const value = (raw || '').trim();
    this.inputValue = '';
    if (!value || !this.control) {
      return;
    }
    if (this.uniqueChips && this.chips.some(c => c.toLowerCase() === value.toLowerCase())) {
      return;
    }
    this.control.setValue([...this.chips, value]);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  removeChip(index: number): void {
    if (!this.control) {
      return;
    }
    const next = this.chips.slice();
    next.splice(index, 1);
    this.control.setValue(next);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  // =================================================================
  // SHARED
  // =================================================================
  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    // In chips mode, commit any pending text so a half-typed chip is not lost
    if (this.hasChips && this.inputValue) {
      this.addChip(this.inputValue);
    }
    this.isFocused = false;
    if (this.control) {
      this.control.markAsTouched();
    }
  }

  get hasError(): boolean {
    return this.control && this.control.invalid && this.control.touched;
  }

  get errorMessage(): string {
    if (!this.control || !this.hasError) {
      return '';
    }

    const errors = this.control.errors;
    if (errors?.required) {
      return 'This field is required';
    }
    if (errors?.email) {
      return 'Please enter a valid email';
    }
    if (errors?.minlength) {
      return `Minimum length is ${errors.minlength.requiredLength}`;
    }
    if (errors?.maxlength) {
      return `Maximum length is ${errors.maxlength.requiredLength}`;
    }
    return 'Invalid value';
  }
}
